/**
 * Practice locations: the places a group practises from (tasks/practice-locations.md). An appointment type's
 * `locationIds` limits it to some of them; null or empty means every location.
 */

/** The ids a type is limited to that are still active locations. Empty means the type is offered everywhere. */
const activeLimits = (type: any, locations: any[]): string[] => {
  const active = new Set(locations.map((l) => l.id));
  return (type?.locationIds || []).filter((id: string) => active.has(id));
};

/**
 * The appointment types to offer at a location. With no location chosen every type is offered. `keepTypeId` keeps the
 * type an existing appointment already has, so editing it does not blank its type.
 */
export function typesForLocation(types: any[], locationId: string | null | undefined, locations: any[], keepTypeId?: string | null): any[] {
  if (!locationId) return types;
  return types.filter((type) => {
    if (keepTypeId && type.id === keepTypeId) return true;
    const limits = activeLimits(type, locations);
    return limits.length === 0 || limits.includes(locationId);
  });
}

/** The group's default location, or null when it has none. */
export function defaultLocationId(locations: any[] | null | undefined): string | null {
  return (locations || []).find((l) => l.isDefault)?.id ?? null;
}

/** Names of the active locations a type is limited to; empty when it is offered everywhere. */
export function typeLocationNames(type: any, locations: any[]): string[] {
  const limits = activeLimits(type, locations);
  return locations.filter((l) => limits.includes(l.id)).map((l) => l.name);
}

/**
 * The rows the calendar shows for a location. All locations (null) or a group without locations keeps everything.
 * Appointments with no location, or one that has since been removed, count as being at the default location. Rows
 * that are not patient appointments (the practice's own events) are kept.
 */
export function appointmentsAtLocation<T extends { patientId?: string; locationId?: string | null; Patient?: { id?: string } }>(
  rows: T[],
  locationId: string | null | undefined,
  locations: any[],
): T[] {
  if (!locationId || !locations.length) return rows;
  const active = new Set(locations.map((l) => l.id));
  const fallback = defaultLocationId(locations);
  return rows.filter((row) => {
    if (!(row.patientId ?? row.Patient?.id)) return true;
    const at = row.locationId && active.has(row.locationId) ? row.locationId : fallback;
    return at === locationId;
  });
}
